<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KTag from "@/components/KTag.vue";
    import cloneDeep from "lodash/cloneDeep";
    import { computed, ref, toRefs } from "vue";

    const props = withDefaults(
        defineProps<{
            name: string;
            /**
             * Knappar som renderas uppe till höger och sätter en grupp med filter params vid klick
             */
            quickActions?: Array<{
                name: string;
                label: string;
                action: () => void;
            }>;
            /**
             * Dölj sökfältet (default false)
             */
            hideSearch?: boolean;
            /**
             * Placeholder som renderas i sökfältet
             */
            searchPlaceholder?: string;
            /**
             * Form schema
             */
            schema: Array<{ [key: string]: unknown }>;
            /**
             * Värde som sätts vid mount
             */
            defaultValue?: {
                [key: string]: any;
            };
            /**
             * Namn på fält som ska behålla sina värden vid rensning av resten av filtret. T.ex
             * "Visa strukna"-värdet som sätts utifrån i opkoordinering ska alltid behållas
             */
            keepOnClear?: string[];
            /**
             * Namn på tabellen som använder sig av filtret. Används för att hämta sparade inställningar för tabellen
             * från session storage och sätter det i default value
             */
            tableStateName?: string;
            /**
             * Ska valda filtreringsalternativ visas som taggar?
             */
            showTags?: boolean;
            /**
             * Ska filtret kunna expanderas och visa filteralternativ? false döljer knappen för att expandera
             */
            expandable?: boolean;
            /**
             * Ska filtret vara expanderat vid mount?
             */
            expanded?: boolean;
            /**
             * Namnet parametern vid fritextsök ska få när det skickas till BE
             */
            searchParam?: string;
            /**
             * Namn på fält vars taggar inte ska kunna rensas av användaren
             */
            nonClearableTags?: string[];
        }>(),
        {
            showTags: true,
            expandable: true,
            searchParam: "q",
        }
    );

    const emit = defineEmits<{
        (event: "submit", payload: { formData: any }): void;
    }>();

    const { defaultValue, tableStateName } = toRefs(props);

    const isExpanded = ref(props.expanded ?? false);
    const formRef = ref();
    const tags = ref<Array<{ label: string; value: string; field: string; displayLabel: string; showValue: boolean }>>(
        []
    );

    /**
     * Konfiguration per fält som kan sättas i schemat:
     *   nonClearableTag      – taggen saknar rensningsknapp
     *   tagShowValue         – om false visas bara label, inte värdet (default: true om värdet inte är boolean true)
     *   tagValueFormatter    – funktion som omvandlar råvärdet till ett läsbart visningsvärde i taggen
     */
    interface FieldSchemaConfig {
        nonClearableTag?: boolean;
        tagShowValue?: boolean;
        tagValueFormatter?: (value: string) => string;
    }

    /**
     * Går igenom schemat rekursivt och plockar ut fältkonfigurationer (t.ex. nonClearableTag och tagShowValue)
     * för varje namngivet fält. Returnerar en Map med fältnamn som nyckel och konfigurationsobjekt som värde.
     */
    const getFieldConfigsFromSchema = (schema: Array<{ [key: string]: unknown }>): Map<string, FieldSchemaConfig> => {
        const result = new Map<string, FieldSchemaConfig>();
        const traverse = (items: Array<{ [key: string]: unknown }>) => {
            items.forEach((item) => {
                if (item.name) {
                    const config: FieldSchemaConfig = {};
                    if (item.nonClearableTag !== undefined) config.nonClearableTag = item.nonClearableTag as boolean;
                    if (item.tagShowValue !== undefined) config.tagShowValue = item.tagShowValue as boolean;
                    if (typeof item.tagValueFormatter === "function")
                        config.tagValueFormatter = item.tagValueFormatter as (value: string) => string;
                    // Spara bara konfigurationen om minst ett alternativ är satt
                    if (Object.keys(config).length) result.set(item.name as string, config);
                }
                // Traversera barn-element rekursivt
                if (Array.isArray(item.children)) {
                    traverse(item.children as Array<{ [key: string]: unknown }>);
                }
            });
        };
        traverse(schema);
        return result;
    };

    const fieldConfigs = computed(() => getFieldConfigsFromSchema(props.schema));

    // Sant om det finns minst en tag som användaren kan rensa (dvs. inte är ett nonClearable-fält)
    const hasClearableTags = computed(() => tags.value.some((tag) => !nonClearableFields.value.includes(tag.field)));

    /**
     * Sammanställer alla fält vars taggar inte ska kunna rensas av användaren.
     * Kombinerar fält markerade med nonClearableTag i schemat med de som skickas in via prop nonClearableTags.
     * Dubbletter tas bort med Set.
     */
    const nonClearableFields = computed(() => {
        const fromSchema = [...fieldConfigs.value.entries()]
            .filter(([, config]) => config.nonClearableTag)
            .map(([name]) => name);
        return [...new Set([...fromSchema, ...(props.nonClearableTags ?? [])])];
    });

    const toggleExpand = () => {
        isExpanded.value = !isExpanded.value;
    };

    const collapse = () => {
        isExpanded.value = false;
    };

    const expand = () => {
        isExpanded.value = true;
    };

    /**
     * Vid klick på en av quick actions
     *
     * values är de filterparametrar som quick actionen vill sätta
     * keep är vilka fält som ska behålla sina värden
     */
    const onQuickAction = async ({ values, keep }: { values: { [key: string]: any }; keep?: string[] }) => {
        // Slå ihop props.keepOnClear och keep till lista med fält som ska behållas
        const allKeep = [...new Set([...(props.keepOnClear ?? []), ...(keep ?? [])])];

        // Hämta ut värden på de fält som ska behållas
        const keptValues = allKeep.map((name) => {
            return [name, formRef?.value?.getFieldValue?.({ name })];
        });

        // Sätt både de nya värdena från quick actionen och de värden som ska behållas
        formRef?.value?.setFormValues({
            values: {
                ...values,
                ...Object.fromEntries(keptValues),
            },
            reset: true,
        });

        // Skicka filterdata och kollapsa filtret
        onSubmit({
            formData: await formRef?.value?.manualSubmit(),
            visibility: "collapse",
        });
    };

    const onSubmit = ({
        formData,
        visibility = "toggle",
    }: {
        formData: any;
        visibility?: "collapse" | "expand" | "toggle" | "keep";
    }) => {
        if (formData) {
            emit("submit", { formData });

            if (visibility === "toggle") {
                toggleExpand();
            } else if (visibility === "expand") {
                expand();
            } else if (visibility === "collapse") {
                collapse();
            }
        }
    };

    /**
     * Vid ändring av sökfältets värde
     */
    const onSearchValue = ({ value }: { value: string }) => {
        formRef?.value?.setFieldValue({ name: props.searchParam, value });
    };

    /**
     * När formuläret med alla filterparametrar ändrar värde. Används för att plocka ut nuvarande form data
     * och renderar tags
     */
    const onFormChange = (formData: any) => {
        const filteredData = {
            fieldLabels: {},
            ...(formRef?.value?.manualFormDataConvert({ displayMode: true, data: formData }) || {}),
        };

        const { fieldLabels, ...data } = filteredData;
        const entries = Object.entries(data);

        tags.value = entries
            // Filtrera bort fält som saknar label eller har ett tomt/falskt värde
            .filter(([field, value]) => {
                if (!fieldLabels[field]) return false;
                if (value === false || value === null || value === undefined || value === "") return false;
                if (Array.isArray(value) && value.length === 0) return false;
                return true;
            })
            .map(([field, value]) => {
                const config = fieldConfigs.value.get(field);
                // tagShowValue: explicit satt eller default (dölj värdet om det är boolean true)
                const showValue = config?.tagShowValue !== undefined ? config.tagShowValue : value !== true;

                if (Array.isArray(value)) {
                    return value.map((v) => {
                        if (typeof v === "object") {
                            const typedValue = v as { displayLabel?: string; value: string };
                            return {
                                field,
                                label: fieldLabels[field],
                                displayLabel: typedValue?.displayLabel ?? typedValue?.value,
                                value: typedValue?.value,
                                showValue,
                            };
                        }
                        return {
                            field,
                            label: fieldLabels[field],
                            displayLabel: (v as { displayLabel?: string })?.displayLabel ?? v,
                            value: v,
                            showValue,
                        };
                    });
                }

                if (typeof value === "object") {
                    const typedValue = value as { displayLabel?: string; value: string };
                    return {
                        field,
                        label: fieldLabels[field],
                        displayLabel: typedValue?.displayLabel ?? typedValue?.value,
                        value: typedValue?.value,
                        showValue,
                    };
                }

                return {
                    field,
                    label: fieldLabels[field],
                    displayLabel: config?.tagValueFormatter
                        ? config.tagValueFormatter(value as string)
                        : ((value as { displayLabel?: string })?.displayLabel ?? value),
                    value: value,
                    showValue,
                };
            })
            .flat() as Array<{
            label: string;
            value: string;
            field: string;
            displayLabel: string;
            showValue: boolean;
        }>;
    };

    const initialValuesGetter = () => {
        let values: { [key: string]: string } = {};

        if (tableStateName?.value) {
            const state = sessionStorage.getItem(tableStateName.value);

            if (state) {
                const parsedState = JSON.parse(state);

                if (parsedState.sortField) {
                    // BE vill ha "ordering" och i det här formatet
                    values.ordering = `${parsedState.sortOrder > 0 ? "" : "-"}${parsedState.sortField}`;
                }
            }
        }

        if (defaultValue?.value) {
            const defaults = cloneDeep(defaultValue.value);

            values = {
                ...values,
                ...defaults,
            };
        }

        return values;
    };

    /**
     * När en tag rensas (via klick på kryss)
     */
    const onTagClear = async ({ field, value }: { field: string; value: string }) => {
        const currentValue = formRef?.value?.getFieldValue?.({
            name: field,
        });

        if (Array.isArray(currentValue)) {
            formRef?.value?.setFieldValue?.({
                name: field,
                value: currentValue.filter((v) => v !== value),
            });
        } else {
            formRef?.value?.setFieldValue?.({
                name: field,
                value: null,
            });
        }

        onSubmit({ formData: await formRef?.value?.manualSubmit?.(), visibility: "keep" });
    };

    const onClearAll = async () => {
        const fieldsToKeep = [...new Set([...(props.keepOnClear ?? []), ...nonClearableFields.value])];
        const keptValues = fieldsToKeep.map((name) => [name, formRef?.value?.getFieldValue?.({ name })]);

        formRef.value.setFormValues({ values: Object.fromEntries(keptValues), reset: true });

        onSubmit({ formData: await formRef?.value?.manualSubmit?.(), visibility: "keep" });
    };

    const onSearchEnter = async () => {
        onSubmit({ formData: await formRef?.value?.manualSubmit?.(), visibility: "collapse" });
    };

    const onFormSubmit = (formData: any) => {
        onSubmit({ formData, visibility: "collapse" });
    };

    /**
     * Sorterar object entries på namn (key)
     */
    const sortEntries = ({ entries }: { entries: [string, any][] }) => {
        return entries.sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1));
    };

    /**
     * Returnerar true om värden som skickas in matchar värden i formuläret
     * (keepOnClear filtreras bort innan jämförelsen görs).
     * Används av quick actions för att t.ex sätta specific class. Exempel:
     * <KButton
     *         :class="{
     *             'is-active': isActive({
     *                 values: {
     *                     typ: 'ANDRAS',
     *                 },
     *             }),
     *         }"
     *         type="button"
     *         variant="selectable"
     *         size="adaptable"
     *         @click="
     *             callback({
     *                 values: {
     *                     typ: 'ANDRAS',
     *                 },
     *             })
     *         "
     *     >
     *         Andras mallar
     *     </KButton>
     */
    const isActive = ({ values }: { values: { [key: string]: any } }) => {
        if (!formRef?.value?.manualFormDataConvert) {
            return false;
        }

        const formData = formRef.value.manualFormDataConvert();
        const filteredFormData = Object.fromEntries(
            sortEntries({
                entries: Object.entries(formData).filter(
                    ([field]) =>
                        (!props.keepOnClear || !props.keepOnClear.includes(field)) && field !== props.searchParam
                ),
            })
        );

        // Manuellt inmatad text i sökfältet ska inte påverka markering av aktiv quick action
        let filteredValues = Object.fromEntries(sortEntries({ entries: Object.entries({ ...values }) }));
        delete filteredValues[props.searchParam];

        return JSON.stringify(filteredFormData) === JSON.stringify(filteredValues);
    };

    const getFormId = () => `${props.name}-filter-form`;

    defineExpose({
        setFieldValue: (props: {
            name: string; // Namn på fältet
            value: unknown; // Nya värdet
        }) => formRef?.value?.setFieldValue?.(props),
        manualSubmit: () => formRef?.value?.manualSubmit?.(),
        onClearAll: () => onClearAll(),
        getFormId,
    });
</script>

<template>
    <div :class="['k-filter', { expanded: isExpanded }]">
        <div :class="['top-row', { expanded: isExpanded }]">
            <slot name="top-row">
                <div class="actions">
                    <slot v-if="expandable" name="expand-button">
                        <KButton type="button" class="expand-button" size="secondary" @click="toggleExpand">
                            <img alt="Expandera sökfilter" src="@/assets/dropdown-arrow-white.svg" />
                            <span>Filter</span>
                            <span class="counter">{{ new Map(tags.map((tag) => [tag.label, tag.value])).size }}</span>
                        </KButton>
                    </slot>
                    <div class="search">
                        <KText
                            v-if="!hideSearch"
                            ignoreEmptyData
                            :name="searchParam"
                            :placeholder="searchPlaceholder ?? 'Sök'"
                            :registerOnMountId="`${name}-filter-form`"
                            @inputValue="onSearchValue"
                            @enter="onSearchEnter"
                        />
                    </div>
                </div>
                <div class="quick-actions">
                    <slot name="quick-actions" :callback="onQuickAction" :isActive="isActive" />
                </div>
            </slot>
        </div>
        <div :class="['content', { expanded: isExpanded }]">
            <KForm
                ref="formRef"
                :id="`${name}-filter-form`"
                class="filter-form"
                submitContent="Filtrera"
                :schema="schema"
                @submit="onFormSubmit"
                @updated="onFormChange"
                :initialValuesGetter="initialValuesGetter"
            >
                <template #header>
                    <slot name="form-header" />
                </template>
                <template #default>
                    <div class="form-content">
                        <slot />
                    </div>
                </template>
                <template #actions>
                    <slot name="form-actions" />
                </template>
            </KForm>
        </div>
        <div v-if="showTags" class="tags">
            <KTag
                v-for="(tag, index) in tags"
                :clearable="!nonClearableFields.includes(tag.field)"
                :key="index"
                contentDirection="horizontal"
                variant="tag"
                @clear="onTagClear({ field: tag.field, value: tag.value })"
            >
                <p>
                    <span>{{ tag.showValue ? `${tag.label}: ` : tag.label }}</span>
                    <span v-if="tag.showValue">{{ tag.displayLabel }}</span>
                </p>
            </KTag>
            <KTag
                v-if="hasClearableTags"
                clearable
                key="rensa"
                contentDirection="horizontal"
                variant="none"
                clearPlacement="start"
                clickableArea="whole"
                @clear="onClearAll"
            >
                <span>Rensa</span>
            </KTag>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/variables";
    @import "@/style/container";

    .k-filter {
        display: grid;
        border-radius: $border-radius-soft;
        max-width: 100%;

        .top-row {
            display: flex;
            padding: 1rem;
            column-gap: 1rem;
            row-gap: 0.5rem;
            align-items: center;
            flex-wrap: wrap;
            background-color: $color-blue-light;

            .actions {
                display: flex;
                gap: 1rem;
                flex-wrap: wrap;

                .expand-button {
                    display: flex;
                    min-width: fit-content;
                    gap: calc($icon-gap-default * 2);
                    flex-grow: 1;

                    img {
                        margin-right: 0;
                    }

                    .counter {
                        display: grid;
                        place-content: center;
                        font-size: 0.8rem;
                        background-color: $color-light;
                        padding: 0.1rem;
                        border-radius: 50%;
                        height: $line-height-default;
                        width: $line-height-default;
                        color: $primary;
                    }
                }

                .search {
                    flex-grow: 1;

                    .k-text {
                        margin: 0;
                    }
                }
            }

            .quick-actions {
                display: flex;
                flex-wrap: wrap;
                flex-grow: 1;
                justify-content: flex-end;
                column-gap: 1rem;
                row-gap: 0.5rem;

                > .button.is-active {
                    font-family:
                        Roboto Medium,
                        sans-serif;
                    font-weight: bold;
                    border-bottom: 3px solid;
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                }
            }
        }

        .content {
            padding: 1rem;
            background-color: $color-blue-light;
            display: none;
            overflow: hidden;

            &.expanded {
                display: initial;
            }
        }

        .tags {
            margin-top: 1rem;
            display: flex;
            flex-wrap: wrap;
            column-gap: 0.5rem;
            row-gap: 0.2rem;
        }
    }
</style>
